import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/api";
import { Loader2, Share2, Scissors, Wand2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface VideoEditorProps {
  open: boolean;
  onClose: () => void;
  videoUrl: string;
  videoId: string;
  videoTitle: string;
}

const VideoEditor: React.FC<VideoEditorProps> = ({ 
  open, 
  onClose, 
  videoUrl, 
  videoId,
  videoTitle
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [startTime, setStartTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isGeneratingHighlights, setIsGeneratingHighlights] = useState<boolean>(false);
  const [highlightSegments, setHighlightSegments] = useState<{start: number, end: number}[]>([]);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [seekPreview, setSeekPreview] = useState<{active: boolean, time: number, direction: 'forward'|'backward'}>({
    active: false,
    time: 0,
    direction: 'forward'
  });
  const { toast } = useToast();

  // Load video metadata on mount
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      const handleLoadedMetadata = () => {
        setDuration(video.duration);
        setEndTime(video.duration);
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [videoRef, open]);

  // Update current time during playback
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
        
        // Pause when reaching end time
        if (video.currentTime >= endTime) {
          video.pause();
          setIsPlaying(false);
          video.currentTime = startTime;
        }
      };
      
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      return () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [videoRef, endTime, startTime]);

  // Play/pause controls
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        if (videoRef.current.currentTime >= endTime) {
          videoRef.current.currentTime = startTime;
        }
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle slider change
  const handleTimeChange = (value: number[]) => {
    if (videoRef.current) {
      const newTime = value[0];
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Handle trim range change
  const handleTrimRangeChange = (values: number[]) => {
    setStartTime(values[0]);
    setEndTime(values[1]);
    
    if (videoRef.current) {
      videoRef.current.currentTime = values[0];
    }
  };

  // Format time display
  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Generate AI highlights
  const generateHighlights = async () => {
    if (!videoId) return;
    
    setIsGeneratingHighlights(true);
    try {
      const response = await apiRequest(`/api/media/${videoId}/highlights`, { method: 'POST' });
      const data = response as any; // Type assertion to work with the response
      
      if (data.segments && Array.isArray(data.segments)) {
        setHighlightSegments(data.segments);
        // Select the first highlight segment if available
        if (data.segments.length > 0) {
          setStartTime(data.segments[0].start);
          setEndTime(data.segments[0].end);
          if (videoRef.current) {
            videoRef.current.currentTime = data.segments[0].start;
          }
          toast({
            title: "Highlights generated!",
            description: `Found ${data.segments.length} exciting moments in your video.`,
          });
        }
      }
    } catch (error) {
      console.error('Error generating highlights:', error);
      toast({
        title: "Highlight generation failed",
        description: "There was an error detecting highlights. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingHighlights(false);
    }
  };

  // Apply a highlight segment
  const applyHighlightSegment = (segment: {start: number, end: number}) => {
    setStartTime(segment.start);
    setEndTime(segment.end);
    if (videoRef.current) {
      videoRef.current.currentTime = segment.start;
    }
  };

  // Save the trimmed clip
  const saveClip = async () => {
    if (!videoId) return;
    
    // Make sure startTime and endTime are valid numbers
    const validStartTime = Number(startTime);
    const validEndTime = Number(endTime);
    
    if (isNaN(validStartTime) || isNaN(validEndTime) || validStartTime >= validEndTime) {
      toast({
        title: "Invalid time range",
        description: "Please make sure the start time is before the end time.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await apiRequest(`/api/media/${videoId}/trim`, {
        method: 'POST',
        body: JSON.stringify({
          startTime: validStartTime,
          endTime: validEndTime,
          title: `${videoTitle} (Clip)`
        })
      });
      
      toast({
        title: "Clip saved successfully!",
        description: "Your new clip has been added to your media library.",
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving clip:', error);
      toast({
        title: "Failed to save clip",
        description: "There was an error saving your clip. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Share to social media
  const shareToSocialMedia = async () => {
    if (!videoId) return;
    
    setIsSharing(true);
    try {
      // Generate a share URL directly without relying on backend processing
      const shareCode = Math.random().toString(36).substring(2, 8);
      const shareUrl = `${window.location.origin}/share/${shareCode}`;
      
      // Open share dialog or copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Share link created!",
        description: "Link copied to clipboard. Ready to paste into your social media.",
      });
      
      // Offer social sharing options
      setTimeout(() => {
        const shareText = `Check out this video from LegacyLoop!`;
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        
        toast({
          title: "Share on social media",
          description: (
            <div className="flex flex-col">
              <p className="mb-2">Share your video on:</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => window.open(twitterUrl, '_blank')}>
                  Twitter
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(facebookUrl, '_blank')}>
                  Facebook
                </Button>
              </div>
            </div>
          ),
          duration: 10000,
        });
      }, 1000);
    } catch (error) {
      console.error('Error creating share link:', error);
      toast({
        title: "Share creation failed",
        description: "There was an error creating your share link. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Video Editor - {videoTitle}</DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          {/* Video player */}
          <div className="relative bg-black rounded-lg overflow-hidden">
            <div 
              className="video-container relative"
              onTouchStart={(e) => {
                const touchX = e.touches[0].clientX;
                const containerWidth = e.currentTarget.clientWidth;
                const touchXPercent = touchX / containerWidth;
                
                // Store initial touch position for swipe detection
                e.currentTarget.setAttribute('data-touch-start-x', touchX.toString());
                e.currentTarget.setAttribute('data-touch-start-time', Date.now().toString());
              }}
              onTouchMove={(e) => {
                const touchX = e.touches[0].clientX;
                const startX = parseFloat(e.currentTarget.getAttribute('data-touch-start-x') || '0');
                const containerWidth = e.currentTarget.clientWidth;
                
                // Calculate swipe distance as percentage of container width
                const swipeDelta = (touchX - startX) / containerWidth;
                
                // Preview seek position on long swipes
                if (Math.abs(swipeDelta) > 0.05 && videoRef.current) {
                  const videoDuration = videoRef.current.duration;
                  const seekDelta = swipeDelta * 15; // 15 seconds per full width swipe
                  const newTime = Math.max(0, Math.min(videoDuration, currentTime + seekDelta));
                  
                  // Show seek preview indicator
                  setSeekPreview({
                    active: true,
                    time: newTime,
                    direction: swipeDelta > 0 ? 'forward' : 'backward'
                  });
                }
              }}
              onTouchEnd={(e) => {
                const touchEndX = e.changedTouches[0].clientX;
                const startX = parseFloat(e.currentTarget.getAttribute('data-touch-start-x') || '0');
                const startTime = parseFloat(e.currentTarget.getAttribute('data-touch-start-time') || '0');
                const containerWidth = e.currentTarget.clientWidth;
                const swipeDuration = Date.now() - startTime;
                
                // Calculate swipe distance as percentage of container width
                const swipeDelta = (touchEndX - startX) / containerWidth;
                
                // Handle swipe gesture for seeking
                if (Math.abs(swipeDelta) > 0.1 && swipeDuration < 300 && videoRef.current) {
                  const videoDuration = videoRef.current.duration;
                  const seekDelta = swipeDelta * 15; // 15 seconds per full width swipe
                  const newTime = Math.max(0, Math.min(videoDuration, currentTime + seekDelta));
                  videoRef.current.currentTime = newTime;
                }
                
                // Reset seek preview
                setSeekPreview({ active: false, time: 0, direction: 'forward' });
                
                // Double tap detection for play/pause
                const touchTime = Date.now();
                const lastTapTime = parseFloat(e.currentTarget.getAttribute('data-last-tap-time') || '0');
                const lastTapX = parseFloat(e.currentTarget.getAttribute('data-last-tap-x') || '0');
                
                // Check if this is a double tap (within 300ms and close to the same position)
                if (touchTime - lastTapTime < 300 && Math.abs(touchEndX - lastTapX) < 30) {
                  togglePlayPause();
                }
                
                // Store this tap for future double-tap detection
                e.currentTarget.setAttribute('data-last-tap-time', touchTime.toString());
                e.currentTarget.setAttribute('data-last-tap-x', touchEndX.toString());
              }}
            >
              <video 
                ref={videoRef}
                src={videoUrl}
                className="w-full h-auto max-h-[60vh]"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                preload="metadata"
              />
              
              {/* Seek preview indicator - only shown during swipe */}
              {seekPreview.active && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/70 text-white px-4 py-2 rounded-full flex items-center">
                  {seekPreview.direction === 'forward' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <polygon points="5 4 15 12 5 20 5 4"></polygon>
                      <line x1="19" y1="5" x2="19" y2="19"></line>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <polygon points="19 20 9 12 19 4 19 20"></polygon>
                      <line x1="5" y1="19" x2="5" y2="5"></line>
                    </svg>
                  )}
                  {formatTime(seekPreview.time)}
                </div>
              )}
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
              <div className="flex items-center justify-between text-white">
                <span>{formatTime(currentTime)}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={togglePlayPause}
                  className="text-white hover:bg-white/20"
                >
                  {isPlaying ? 'Pause' : 'Play'}
                </Button>
                <span>{formatTime(duration)}</span>
              </div>
              
              <Slider 
                value={[currentTime]} 
                min={0} 
                max={duration} 
                step={0.1} 
                onValueChange={handleTimeChange}
                className="mt-1"
              />
            </div>
          </div>
          
          {/* Trim controls */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Trim Range</h3>
              <div className="text-sm">
                {formatTime(startTime)} - {formatTime(endTime)} ({(endTime - startTime).toFixed(1)}s)
              </div>
            </div>
            
            <Slider 
              value={[startTime, endTime]} 
              min={0} 
              max={duration} 
              step={0.1} 
              onValueChange={handleTrimRangeChange}
            />
          </div>
          
          {/* AI Highlights */}
          {highlightSegments.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">AI Highlights</h3>
              <div className="flex flex-wrap gap-2">
                {highlightSegments.map((segment, index) => (
                  <Button 
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => applyHighlightSegment(segment)}
                  >
                    Highlight {index + 1} ({(segment.end - segment.start).toFixed(1)}s)
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <Separator className="my-2" />
        
        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={generateHighlights}
              disabled={isGeneratingHighlights}
              className="flex items-center gap-1"
            >
              {isGeneratingHighlights ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Detecting...</span>
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" />
                  <span>Detect Highlights</span>
                </>
              )}
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              onClick={shareToSocialMedia}
              disabled={isSharing}
              className="flex items-center gap-1"
            >
              {isSharing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Creating Share...</span>
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4" />
                  <span>Share to Social</span>
                </>
              )}
            </Button>
            
            <Button
              variant="default"
              onClick={saveClip}
              disabled={isSaving}
              className="flex items-center gap-1"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Scissors className="h-4 w-4" />
                  <span>Save Clip</span>
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoEditor;